import {
  CardStatusEnum,
  MyProjectsProject,
  ProTeamCard,
  RepTeamCard,
} from '@platform/client/schemas';
import { Card, CardBadge, PillBadgeColors, StandardButtonProps } from '@platform/components';
import { useContext } from 'react';
import { ProjectType } from '.';
import dataTestids from '../../constants/test-ids';
import { UpdateProjectDatesModalContext } from './Modals/UpdateProjectDatesModal';

export const ProUserTeamCard = ({ cardData }: { cardData: ProTeamCard }) => {
  const { project, avatar, organization } = cardData;
  const { openUpdateProjectDatesModal } = useContext(UpdateProjectDatesModalContext);

  if (!project.startDate || !project.endDate) {
    return null;
  }

  const completed = cardData.cardStatus === CardStatusEnum.project_complete;
  const projectEndDate = new Date(project.endDate);

  const [headerText, headerColor] = getHeaderTextAndColor(completed, projectEndDate, 'pro');
  const [buttonText, buttonVariant] = getButtonTextAndVariant(completed);

  const [statusBadgeText, statusBadgeColor] = getStatusBadgeTextAndColor(completed);

  const badges: CardBadge[] = [
    {
      text: project.roleType ? ProjectType[project.roleType] : ProjectType.doer,
      color: 'secondary-brand-25',
    },
    {
      text: statusBadgeText,
      color: statusBadgeColor,
    },
  ];

  const headerContent = <div className='font-medium'>{headerText}</div>;

  const [footerText, _footerColor] = getFooterText(completed, cardData);

  return (
    <Card
      headerContent={headerContent}
      headerColor={headerColor}
      avatarSize='md'
      avatarHref={avatar || ''}
      content={project.name}
      contentTextHref={`/platform/directory/projects/${project.id}`}
      contentSubText={organization.name}
      contentSubtextHref={`/platform/directory/organizations/${organization.id}`}
      badges={badges}
      variant='large'
      buttonText={buttonText}
      buttonVariant={buttonVariant}
      buttonOnClick={() =>
        openUpdateProjectDatesModal({
          userType: 'experteer',
          isTeam: true,
          organizationName: organization.name,
          id: project.id,
          startDate: project.startDate ? new Date(project.startDate) : new Date(),
          endDate: projectEndDate,
        })
      }
      footerText={footerText}
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project.id}`}
    />
  );
};

export const RepUserTeamCard = ({
  project,
  cardData,
}: {
  project: MyProjectsProject;
  cardData: RepTeamCard;
}) => {
  // const { openUpdateProjectDatesModal } = useModalActions().updateProjectDatesModalActions;
  const { openUpdateProjectDatesModal } = useContext(UpdateProjectDatesModalContext);
  // assignments should not exist without an end date
  if (!project.startDate || !project.endDate) {
    return;
  }

  const completed = !!(new Date(project.endDate).getTime() < new Date().getTime());

  const projectEndDate = new Date(project.endDate);

  const [headerText, headerColor] = getHeaderTextAndColor(completed, projectEndDate, 'org');
  const [buttonText, buttonVariant] = getButtonTextAndVariant(completed);

  const [statusBadgeText, statusBadgeColor] = getStatusBadgeTextAndColor(completed);

  const introductionSubtext = getIntroductionSubtext(new Date(cardData.cardStatusChangedAt));
  const badges: CardBadge[] = [
    {
      text: statusBadgeText,
      color: statusBadgeColor,
    },
  ];

  const headerContent = <div className='font-medium'>{headerText}</div>;

  return (
    <Card
      headerContent={headerContent}
      headerColor={headerColor}
      buttonText={buttonText}
      buttonVariant={buttonVariant}
      buttonOnClick={() =>
        openUpdateProjectDatesModal({
          userType: 'organization',
          isTeam: true,
          id: project.id,
          startDate: project.startDate ? new Date(project.startDate) : new Date(),
          endDate: projectEndDate,
        })
      }
      content={'Experteer Team'}
      avatarSize='md'
      avatarHref={''}
      contentSubText={introductionSubtext}
      badges={badges}
      variant='small'
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project.id}`}
    />
  );
};

const getHeaderTextAndColor = (
  completed: boolean,
  endDate: Date,
  userType: 'pro' | 'org',
  // team: OrgUserTeamIntroData
): [React.ReactNode, string] => {
  if (!completed) {
    return ['Your project is in progress! Let us know if you need any help', 'warning-50'];
  } else {
    return [
      <>
        {`This project ${
          userType === 'org' ? 'with an Experteer Team' : ''
        } was completed ${endDate.toLocaleDateString('en-CA', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`}
      </>,
      'success-25',
    ];
  }
};

const getButtonTextAndVariant = (completed: boolean): [string, StandardButtonProps['variant']] => {
  if (!completed) {
    return ['Update Dates', 'primary'];
  } else {
    return ['', 'primary'];
  }
};

const getStatusBadgeTextAndColor = (completed: boolean): [string, PillBadgeColors] => {
  if (completed) {
    return ['Completed', 'success-100'];
  } else {
    return ['Matched', 'success-25'];
  }
};
const getIntroductionSubtext = (statusChangedAt: Date) => {
  return `Matched ${statusChangedAt.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

const getFooterText = (completed: boolean, cardData: RepTeamCard | ProTeamCard) => {
  if (completed) {
    return [
      `Completed ${new Date(cardData.cardStatusChangedAt).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`,
      'success-25',
    ];
  } else {
    // TODO: I think this is a bug; this UI shouldn't even render if we don't have a teamAssignment
    const matchDate = new Date(cardData.cardStatusChangedAt);
    return [
      `Matched ${matchDate.toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`,
      'warning-50',
    ];
  }
};
